/** Auth-flow tracer (diagnostic). Logs to the browser console AND beacons each
 *  event to the console-api `/debug/auth-trace` sink, which logs it to CloudWatch
 *  with an `AUTH_TRACE` marker.
 *
 *  Why the beacon: the sign-in flow is a chain of full-page, cross-origin
 *  redirects (console → Guardius `/auth/authorize` → back, or a bounce to the
 *  onboarding wizard on `reason=no_tenant`). The browser console is wiped on each
 *  navigation and we get nothing from Guardius's own page, so client logs alone
 *  can't be followed end-to-end. `navigator.sendBeacon` fires before the
 *  navigation and survives it, so the server-side trail is the reliable record.
 *
 *  A per-tab correlation id ties the events of one sign-in attempt together in
 *  CloudWatch. Best-effort throughout: never throws, never blocks navigation,
 *  never logs token VALUES (only derived flags / the tenant claim).
 *
 *  Added for the owner-sign-in `no_tenant` investigation — KEEP until explicitly
 *  told to remove it. */

const CID_KEY = 'console.trace.cid';

function correlationId(): string {
  try {
    let cid = sessionStorage.getItem(CID_KEY);
    if (!cid) {
      cid = Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);
      sessionStorage.setItem(CID_KEY, cid);
    }
    return cid;
  } catch {
    return 'no-cid';
  }
}

export function authTrace(
  apiUrl: string | undefined,
  event: string,
  data?: Record<string, unknown>,
): void {
  const payload = {
    cid: correlationId(),
    event,
    t: new Date().toISOString(),
    path: typeof location !== 'undefined' ? location.pathname : '',
    search: typeof location !== 'undefined' ? location.search : '',
    ...data,
  };
  try {
    // eslint-disable-next-line no-console
    console.log(`[AUTH-TRACE] ${event}`, payload);
  } catch {
    /* ignore */
  }
  try {
    if (apiUrl && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // text/plain keeps it a CORS-simple request (no preflight); the server
      // reads the body as JSON. We never read the response.
      const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
      navigator.sendBeacon(`${apiUrl}/debug/auth-trace`, blob);
    }
  } catch {
    /* ignore */
  }
}
